import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Activity, RefreshCw, MessageCircle, Mail, Search, CheckCircle2, XCircle, CalendarClock, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' })
      + ' · ' + d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export default function CommunicationLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState('30');
  const [channel, setChannel] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/communication-log`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days, channel, search: search || undefined, limit: 200 },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Fehler beim Laden des Kommunikations-Protokolls');
    } finally {
      setLoading(false);
    }
  }, [days, channel, search, token]);

  useEffect(() => {
    const t = setTimeout(load, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/communication-log/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days, channel, search: search || undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kommunikationslog_${days}tage.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export erstellt');
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const triggerColor = (trigger) => {
    if (!trigger) return 'bg-gray-100 text-gray-600';
    if (trigger.includes('change')) return 'bg-amber-100 text-amber-700';
    if (trigger.includes('cancel')) return 'bg-red-100 text-red-700';
    if (trigger.includes('confirm')) return 'bg-emerald-100 text-emerald-700';
    if (trigger.includes('reminder')) return 'bg-blue-100 text-blue-700';
    if (trigger.includes('invoice')) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="communication-log-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-teal-600" />
            Kommunikations-Protokoll
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Alle versendeten WhatsApp- und E-Mail-Nachrichten mit <strong>Auslöser</strong> (warum),
            Empfänger, Status und Bezug zum Termin/Kunden.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="comm-log-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
        <Button onClick={exportCsv} disabled={exporting} data-testid="comm-log-export" className="bg-teal-600 hover:bg-teal-700 text-white">
          <Download className={`h-4 w-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} /> CSV-Export
        </Button>
      </div>

      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Name, Nummer, E-Mail oder Inhalt suchen…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="comm-log-search"
              />
            </div>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="w-[150px]" data-testid="comm-log-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kanäle</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">E-Mail</SelectItem>
              </SelectContent>
            </Select>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[150px]" data-testid="comm-log-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Letzte 7 Tage</SelectItem>
                <SelectItem value="30">Letzte 30 Tage</SelectItem>
                <SelectItem value="90">Letzte 90 Tage</SelectItem>
                <SelectItem value="365">Letztes Jahr</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <CardTitle className="text-sm text-gray-500 font-normal mb-3">
            {loading ? 'Lädt…' : `${items.length} von ${total} Nachricht(en)`}
          </CardTitle>
          {loading ? (
            <p className="text-sm text-gray-400 py-8 text-center">Lädt…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-400 py-12 text-center" data-testid="comm-log-empty">Keine Nachrichten im gewählten Zeitraum.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-3 whitespace-nowrap">Zeit</th>
                    <th className="py-2 pr-3">Kanal</th>
                    <th className="py-2 pr-3">Empfänger</th>
                    <th className="py-2 pr-3">Auslöser</th>
                    <th className="py-2 pr-3">Inhalt</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3 text-right">Bezug</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={it.id || idx} className="border-b last:border-0 hover:bg-gray-50 align-top" data-testid="comm-log-row">
                      <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{fmtDate(it.sent_at)}</td>
                      <td className="py-2 pr-3">
                        {it.channel === 'whatsapp'
                          ? <span className="inline-flex items-center gap-1 text-green-600"><MessageCircle size={15} /> WA</span>
                          : <span className="inline-flex items-center gap-1 text-blue-600"><Mail size={15} /> Mail</span>}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="font-medium">{it.recipient_name || '—'}</div>
                        <div className="text-xs text-gray-400">{it.recipient}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <Badge className={triggerColor(it.trigger)}>{it.trigger_label}</Badge>
                      </td>
                      <td className="py-2 pr-3 max-w-[280px] text-gray-600">
                        <div className="truncate" title={it.content}>{it.content || '—'}</div>
                      </td>
                      <td className="py-2 pr-3">
                        {it.status === 'sent'
                          ? <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 size={14} /> Gesendet</span>
                          : <span className="inline-flex items-center gap-1 text-red-600"><XCircle size={14} /> {it.status}</span>}
                      </td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        {it.customer_id && (
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/customers?id=${it.customer_id}`)} data-testid="comm-log-customer-link">
                            Kunde
                          </Button>
                        )}
                        {it.appointment_id && (
                          <Button variant="ghost" size="sm" className="text-teal-600" onClick={() => navigate('/appointments')} title={`Termin: ${it.appointment_id}`} data-testid="comm-log-appt-link">
                            <CalendarClock size={15} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
